import { useCallback, useEffect, useState } from "react";
import { Option } from "../../models/generics";
import { useToasts } from "../../contexts/toasts";
import { Input, Select } from "antd";
import { BdaModel, ModelTask } from "../../models/BdaModel";
import { Col, Form, Row } from "react-bootstrap";
import { RedisVariables } from "../../api/RedisVariables";
import { Models } from "../../api/Models";
import { cloneDeep, set } from "lodash";

type Props = {
  model: BdaModel;
  onModelChange: React.Dispatch<React.SetStateAction<BdaModel | undefined>>;
  validated?: boolean;
};

export default function ModelCardSettingsForm({ model, onModelChange, validated }: Props) {
  const { showToast } = useToasts();
  const [formatOptions, setFormatOptions] = useState<{ label: string, value: string }[]>([]);
  const [languages, setLanguages] = useState<Option[]>();
  const [licenseOptions, setLicenseOptions] = useState<Option[]>([]);
  const [tasks, setTasks] = useState<ModelTask[]>();
  const [taskOptions, setTaskOptions] = useState<Option[]>([]);

  const redisKeysNamespace = window.__env.MODEL_CARD_REDIS_NAMESPACE || "system";
  const languageRedisKey = window.__env.MODEL_CARD_LANGUAGE_REDIS_KEY || "language.iso639";
  const modelTasksRedisKey = window.__env.MODEL_CARD_TASKS_REDIS_KEY || "modelTasks";
  const licensesRedisKey = window.__env.MODEL_CARD_LICENSES_REDIS_KEY || "licences";

  const initLanguages = useCallback(() => {
    RedisVariables.getRedisVariable(languageRedisKey, redisKeysNamespace)
      .then(
        ({ data }) => {
          const languageData: Option[] = JSON.parse(data.value);
          setLanguages(languageData);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast("model-languages-error", message, `Model Languages Error ${status ? `(${status})` : ""}`, "danger");
        },
      );
  }, [showToast, languageRedisKey, redisKeysNamespace]);

  const initTasks = useCallback(() => {
    RedisVariables.getRedisVariable(modelTasksRedisKey, redisKeysNamespace)
      .then(
        ({ data }) => {
          const taskData: ModelTask[] = JSON.parse(data.value);
          setTasks(taskData);
          setTaskOptions(taskData.map(task => ({ label: task.value, value: task.key })));
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast("model-tasks-error", message, `Model Tasks Error ${status ? `(${status})` : ""}`, "danger");
        },
      );
  }, [showToast, modelTasksRedisKey, redisKeysNamespace]);

  const initLicenses = useCallback(() => {
    RedisVariables.getRedisVariable(licensesRedisKey, redisKeysNamespace)
      .then(
        ({ data }) => {
          const licensesData: Option[] = JSON.parse(data.value);
          setLicenseOptions(licensesData);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast("model-licenses-error", message, `Model Licenses Error ${status ? `(${status})` : ""}`, "danger");
        },
      );
  }, [showToast, licensesRedisKey, redisKeysNamespace]);

  const initFormats = useCallback(() => {
    Models.getModelsFormats()
      .then(
        ({ data }) => setFormatOptions(data.map(format => ({ label: format, value: format }))),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          setFormatOptions([]);
          showToast('formats-error', message, `Formats Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showToast]);

  useEffect(() => {
    initLanguages();
    initTasks();
    initLicenses();
    initFormats();
  }, [initLanguages, initTasks, initLicenses, initFormats]);

  const handleLicenseChange = useCallback((value: string, option: any) => {
    onModelChange(curr => ({
      ...curr!,
      license: {
        link: curr!.license?.link || "",
        value: option?.label,
        key: value,
      }
    }))
  }, [onModelChange]);

  const handleTaskChange = useCallback((value: string) => {
    const task = (tasks || []).find((t) => t.key === value);
    onModelChange(curr => ({ ...curr!, task }));
  }, [tasks, onModelChange]);

  const handleFieldChange = useCallback((field: string | string[], pure?: boolean) => (evt: any) => {
    const value = pure ? evt : evt.target.value;
    onModelChange(curr => {
      const newModel = cloneDeep(curr!);
      set(newModel, field, value);
      return newModel;
    });
  }, [onModelChange]);

  return (
    <>
      <Row className="gx-2">
        <Col md={6}>
          <Form.Group className="mb-2">
            <Form.Label>Languages</Form.Label>
            <Select
              className="w-100"
              mode="multiple"
              placeholder="Select languages"
              options={languages}
              value={model.languages}
              onChange={handleFieldChange("languages", true)}
              allowClear
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="asset-format">
            <Form.Label>Format</Form.Label>
            <Select
              className="w-100"
              placeholder="Select Format"
              options={formatOptions}
              status={(validated && !model?.format) ? 'error' : undefined}
              onChange={handleFieldChange("format", true)}
            />
            {validated && !model?.format && <Form.Control.Feedback className="d-block" type="invalid">Model format is required.</Form.Control.Feedback>}
          </Form.Group>
        </Col>
      </Row>

      <Row className="gx-2 mb-2">
        <Col md={6}>
          <Form.Group className="mb-2">
            <Form.Label>License</Form.Label>
            <Select
              className="w-100"
              placeholder="Select license"
              onChange={handleLicenseChange}
              options={licenseOptions}
              value={model.license?.key}
              allowClear
            />
          </Form.Group>
        </Col>
        <Col md={6} className="mb-2">
          <Form.Group>
            <Form.Label>Task</Form.Label>
            <Select
              className="w-100"
              onChange={handleTaskChange}
              placeholder="Select task"
              options={taskOptions}
              allowClear
            />
          </Form.Group>
        </Col>
      </Row>

      <Form.Group>
        <Form.Label>License Link</Form.Label>
        <Input placeholder="Enter license link" onChange={handleFieldChange(["license", "link"])} />
      </Form.Group>
    </>
  );
}
